import { useCallback, useEffect, useState } from 'react';
import { useMyLibraryContext } from '@insights_module/context/MyLibraryContext';
import { useLibraryGraphqlAudios } from '@insights_module/hooks/useLibraryGraphqlAudios';
import { useLibraryGraphqlGroups } from '@insights_module/hooks/useLibraryGraphqlGroups';
import { useInsightsContext } from '@insights_module/context/InsightsContext';
import { getGarmDataArray } from '@insights_module/utils/showsUtils';
import { GarmResultsType } from '@insights_module/types/BrandSafety';

// we need this to keep track of the last page of the table when we navigate to the next or previous item.
// If item is the last one on the page, with next action we need to go to the next page.
// If item is the first one on the page, with previous action we need to go to the previous page.
type ItemChangeMetadata = {
  type: 'audios' | 'groups';
  direction: 'next' | 'previous';
  lastPage: number;
};

export const useLibraryTableData = () => {
  const [itemChangeMetadata, setItemChangeMetadata] = useState<ItemChangeMetadata | null>(null);
  const { setSelectedShow, setSelectedEpisode } = useInsightsContext();

  const {
    data: audiosData,
    total: totalAudiosCount = 0,
    isLoading: isAudiosLoading,
    isFetching: isFetchingAudios,
    isPrefetching: isPrefetchingAudios,
    pagination: audiosPagination,
  } = useLibraryGraphqlAudios();

  const {
    data: groupsData,
    total: totalGroupsCount = 0,
    isLoading: isGroupsLoading,
    isFetching: isFetchingGroups,
    isPrefetching: isPrefetchingGroups,
    pagination: groupsPagination,
  } = useLibraryGraphqlGroups();

  const {
    audiosCurrentPage,
    audiosHasNextPage,
    audiosHasPreviousPage,
    audiosPaginationRange,
    audiosTotalPages,
    audiosGoToNextPage,
    audiosGoToPreviousPage,
    audiosGoToPage,

    groupsCurrentPage,
    groupsHasNextPage,
    groupsHasPreviousPage,
    groupsPaginationRange,
    groupsTotalPages,
    groupsGoToNextPage,
    groupsGoToPreviousPage,
    groupsGoToPage,
  } = useMyLibraryContext();

  const getGarmForEpisode = useCallback(
    (id: string) => {
      const episodeWithGarm = audiosData?.find((episode) => episode.id === id);

      if (episodeWithGarm && episodeWithGarm.garm) {
        return {
          results: getGarmDataArray(episodeWithGarm.garm as GarmResultsType),
          isLoading: false,
        };
      }

      return { results: undefined, isLoading: false };
    },
    [audiosData]
  );

  const getEpisodeIndex = (id: string): number => {
    return audiosData?.findIndex((audio) => audio?.details?.id === id) || 0;
  };

  const getEpisodeByIndex = (index: number) => {
    return audiosData?.[index];
  };

  const getEpisodeById = (id: string) => {
    return audiosData?.find((audio) => audio?.id === id);
  };

  const getPrevEpisode = (id: string) => {
    const index = getEpisodeIndex(id);
    if (audiosData && audiosHasPreviousPage && index - 1 < 0) {
      audiosGoToPreviousPage();
      setItemChangeMetadata({
        type: 'audios',
        direction: 'previous',
        lastPage: audiosCurrentPage,
      });
      return;
    }
    return getEpisodeByIndex(index - 1);
  };

  const getNextEpisode = (id: string) => {
    const index = getEpisodeIndex(id);
    if (audiosData && audiosHasNextPage && index + 1 === audiosData.length) {
      setItemChangeMetadata({
        type: 'audios',
        direction: 'next',
        lastPage: audiosCurrentPage,
      });
      audiosGoToNextPage();
      return;
    }
    return getEpisodeByIndex(index + 1);
  };

  const getShowIndex = (id: string): number => {
    return groupsData?.findIndex((group) => group?.id === id) || 0;
  };

  const getShowByIndex = (index: number) => {
    return groupsData?.[index];
  };

  const getShowById = (id: string) => {
    return groupsData?.find((group) => group?.id === id);
  };

  const getPrevShow = (id: string) => {
    const index = getShowIndex(id);
    if (groupsData && groupsHasPreviousPage && index - 1 < 0) {
      groupsGoToPreviousPage();
      setItemChangeMetadata({
        type: 'groups',
        direction: 'previous',
        lastPage: groupsCurrentPage,
      });
      return;
    }
    return getShowByIndex(index - 1);
  };

  const getNextShow = (id: string) => {
    const index = getShowIndex(id);
    if (groupsData && groupsHasNextPage && index + 1 === groupsData.length) {
      setItemChangeMetadata({
        type: 'groups',
        direction: 'next',
        lastPage: groupsCurrentPage,
      });
      groupsGoToNextPage();
      return;
    }
    return getShowByIndex(index + 1);
  };

  useEffect(() => {
    if (
      itemChangeMetadata &&
      itemChangeMetadata.type === 'groups' &&
      itemChangeMetadata.lastPage !== groupsPagination?.page_no
    ) {
      if (itemChangeMetadata.direction === 'next') {
        setSelectedShow(groupsData?.[0]?.id || '');
      } else {
        setSelectedShow(groupsData?.[groupsData.length - 1]?.id || '');
      }
      setItemChangeMetadata(null);
    }
  }, [itemChangeMetadata, groupsData, groupsPagination]);

  useEffect(() => {
    if (
      itemChangeMetadata &&
      itemChangeMetadata.type === 'audios' &&
      itemChangeMetadata.lastPage !== audiosPagination?.page_no
    ) {
      if (itemChangeMetadata.direction === 'next') {
        setSelectedEpisode(audiosData?.[0]?.id || '');
      } else {
        setSelectedEpisode(audiosData?.[audiosData.length - 1]?.id || '');
      }
      setItemChangeMetadata(null);
    }
  }, [itemChangeMetadata, audiosData, audiosPagination]);

  return {
    audiosData,
    totalAudiosCount,
    isAudiosLoading,
    isFetchingAudios,
    isPrefetchingAudios,
    audiosPagination,
    audiosCurrentPage,
    audiosPaginationRange,
    audiosTotalPages,
    audiosGoToNextPage,
    audiosGoToPreviousPage,
    audiosGoToPage,
    getGarmForEpisode,
    groupsData,
    totalGroupsCount,
    isGroupsLoading,
    isFetchingGroups,
    isPrefetchingGroups,
    groupsPagination,
    groupsCurrentPage,
    groupsPaginationRange,
    groupsTotalPages,
    groupsGoToNextPage,
    groupsGoToPreviousPage,
    groupsGoToPage,

    getEpisodeIndex,
    getEpisodeByIndex,
    getEpisodeById,
    getPrevEpisode,
    getNextEpisode,

    getShowIndex,
    getShowByIndex,
    getShowById,
    getPrevShow,
    getNextShow,
  };
};
